import React, { useEffect, useRef, useState, useCallback } from 'react';
import { notify } from '../../../shared/feedback/notify';
import SearchIcon from '../../../img/search-icon.svg';
import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import ArrowRightOrange from '../../../img/arrow-right-orange.svg';
import './BrandPickerModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

export interface PickerItem {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface FipePickerModalProps {
  isOpen: boolean;
  title: string;
  searchPlaceholder: string;
  fetchFn: (params: { search?: string; page: number; limit: number }) => Promise<{
    totalItems: number;
    items: PickerItem[];
  }>;
  onSelect: (item: PickerItem) => void;
  onCancel: () => void;
}

const PAGE_SIZE = 20;

const FipePickerModal: React.FC<FipePickerModalProps> = ({
  isOpen,
  title,
  searchPlaceholder,
  fetchFn,
  onSelect,
  onCancel,
}) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<PickerItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(
    async (searchValue: string, pageValue: number) => {
      setLoading(true);
      try {
        const result = await fetchFn({
          search: searchValue || undefined,
          page: pageValue,
          limit: PAGE_SIZE,
        });
        setItems(result.items);
        setTotalItems(result.totalItems);
      } catch (error) {
        // Sem sinal, a lista aparecia vazia como se não houvesse opções.
        setItems([]);
        setTotalItems(0);
        notify.errorFrom(
          error,
          'Não foi possível carregar a lista de opções. Tente novamente; se persistir, acione a TI.',
        );
      } finally {
        setLoading(false);
      }
    },
    [fetchFn],
  );

  useEffect(() => {
    if (!isOpen) return;
    setSearch('');
    setPage(1);
    load('', 1);
  }, [isOpen, load]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    setPage(1);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => load(value, 1), 350);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    load(search, newPage);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    if (isOpen) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  return (
    <div
      className="picker-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="picker-modal" ref={modalRef}>
        <div className="picker-header">
          <span className="picker-title">{title}</span>
          <button className="close-button" type="button" onClick={onCancel}>
            &times;
          </button>
        </div>
        <div className="picker-search">
          <div className="picker-search-wrapper">
            <img className="picker-search-icon" src={SearchIcon} alt="" width="18" height="18" />
            <input
              className="picker-search-input"
              type="text"
              placeholder={searchPlaceholder}
              value={search}
              onChange={handleSearchChange}
              autoFocus
            />
          </div>
        </div>
        <div className="picker-list">
          {loading ? (
            <div className="picker-loading">
              <div className="spinner"></div>
              <p>Carregando...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="picker-empty">Nenhum resultado encontrado.</div>
          ) : (
            items.map((item) => (
              <button
                key={item.id}
                type="button"
                className="picker-item"
                onClick={() => onSelect(item)}
              >
                {item.name}
              </button>
            ))
          )}
        </div>
        {totalPages > 1 && (
          <div className="picker-pagination">
            <button
              type="button"
              className="picker-page-btn"
              disabled={page <= 1}
              onClick={() => handlePageChange(page - 1)}
            >
              <img src={ArrowLeftOrange} alt="" width="16" height="16" />
              <span>Anterior</span>
            </button>
            <span className="picker-page-info">
              Página {page} de {totalPages}
            </span>
            <button
              type="button"
              className="picker-page-btn"
              disabled={page >= totalPages}
              onClick={() => handlePageChange(page + 1)}
            >
              <span>Próxima</span>
              <img src={ArrowRightOrange} alt="" width="16" height="16" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FipePickerModal;
